"use client";

import "@/studio-config";
import { DataContextProvider } from "@/app/contextProviders/data-context";
import { EntityStore } from "@contentful/experiences-core";
/**
 * `PreviewDeliveryRoot`, not `ExperienceRoot`. `ExperienceRoot` picks between the
 * visual editor and the delivery renderer via `useDetectCanvasMode()`, which listens
 * for a window-global `requestEditorMode` postMessage. That signal means "this
 * browsing context is a Studio canvas", not "this tree is being edited" — so inside
 * Studio the chrome's root flips to the visual editor, discards the server tree and
 * removes the server-rendered element from the document.
 *
 * Confirmed twice by swapping this line for ExperienceRoot: the footer disappears
 * from the canvas while still rendering on ?isPreview=true. ReusableContent nests an
 * ExperienceRoot and survives, most likely because it mounts after Studio has already
 * answered the page root's handshake; a route slot mounts alongside it and gets its own.
 *
 * The SDK does not re-export it; see types/experiences-sdk-internals.d.ts.
 */
import { PreviewDeliveryRoot } from "@contentful/experiences-sdk-react/dist/blocks/preview/PreviewDeliveryRoot";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useMemo } from "react";
import SmileInvocaTelAutoTag from "@/app/components/SmileInvocaTelAutoTag/SmileInvocaTelAutoTag";

const queryClient = new QueryClient();

interface SlotExperienceProps {
	experienceJSON: string;
	locale: string;
	externalData: Record<string, unknown>;
	preview: boolean;
	/**
	 * The slot's CSS as detached by getSlotExperience. Rendered inline ahead of
	 * the tree so the first paint is already styled; the renderer sees the baked
	 * cfSsrClassName and skips its own client-side injection.
	 */
	stylesheet?: string | null;
}

const SlotExperience = ({
	experienceJSON,
	locale,
	externalData,
	preview,
	stylesheet = null,
}: SlotExperienceProps) => {
	// Not `createExperience`: it ends with `resetEntityStore()`, a zustand set().
	// Running that from a useMemo updates RootRenderer during this render, which
	// React warns about, and it replaces the page's store rather than merging.
	// PreviewDeliveryRoot only reads `entityStore` and passes it down explicitly.
	const experience = useMemo(
		() => ({ entityStore: new EntityStore(experienceJSON) }),
		[experienceJSON],
	);

	return (
		<QueryClientProvider client={queryClient}>
			<DataContextProvider data={externalData} preview={preview}>
				{/* Same shape as ExperienceWrapper's #experience-styles: the CSS ships
				    in the server HTML, before the tree it styles, so the slot never
				    paints unstyled and then shifts once the client injects it. */}
				{stylesheet ? <style data-slot-experience-styles="">{stylesheet}</style> : null}
				<PreviewDeliveryRoot experience={experience} locale={locale} />
				{/* Invoca DNI safety net for SLOT-rendered authored chrome. The
				    /smile/schedule dispatcher and the header/footer slots render
				    Contentful content through this component, NOT through
				    ExperienceWrapper/Experience, so without this mount a bare
				    authored tel: container-link published into the shared chrome
				    entries would go live untagged on those surfaces (the exact
				    ClearChoice Friday D1 class). Tagging is idempotent and the
				    component gates itself to /smile paths and out of the studio
				    editor, so multiple mounts per page (header slot + footer
				    slot + Experience) converge with no side effects. */}
				<SmileInvocaTelAutoTag />
			</DataContextProvider>
		</QueryClientProvider>
	);
};

export default SlotExperience;
